import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  CircularProgress,
  Alert,
  Button,
  TextField,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import { useWizard } from '../../context/WizardContext';
import { getAIAnalysis, AIAnalysisResponse } from '../../services/aiAnalysis';
import { useParams } from 'react-router-dom';
import { useAdvisor } from '../../hooks/useAdvisor';
import { formatAdvisorName } from '../../utils/formatAdvisorName';
import planScoutLogo from '../../assets/PlanScout_orange_Logo.jpg';
import { useProgressTracking } from '../../hooks/useProgressTracking';

const ReviewStep: React.FC = () => {
  const { wizardData, updateWizardData } = useWizard();
  const { idCustomer } = useParams<{ idCustomer: string }>();
  const advisor = useAdvisor();
  const hasCustomerId = !!idCustomer;
  const { personal, employment, socialSecurity, savings, goals } = wizardData;
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResponse, setAiResponse] = useState<AIAnalysisResponse | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string>('');
  const [phoneError, setPhoneError] = useState<string>('');
  const [advisorFirstName, setAdvisorFirstName] = useState<string>('');
  const [advisorLastName, setAdvisorLastName] = useState<string>('');
  
  // Initialize progress tracking
  const { trackFieldChange } = useProgressTracking({
    stepName: 'Review & Submit',
    stepNumber: 6
  });

  // Derive wizard path from intakeVersion + automationEnabled (database-driven)
  const isDemoAdvisor = advisor?.intakeVersion === 'demo';
  const isFullAutomation = advisor?.intakeVersion === 'full' && advisor?.automationEnabled === true;

  const formatCurrency = (value: number | undefined) => {
    if (!value) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not provided';
    const date = new Date(dateString);
    // Add the timezone offset to ensure the date is displayed correctly
    date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const calculateAge = (dateString: string) => {
    if (!dateString) return 'Unknown';
    const birthDate = new Date(dateString);
    // Add the timezone offset to ensure the date is calculated correctly
    birthDate.setMinutes(birthDate.getMinutes() + birthDate.getTimezoneOffset());
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age.toString();
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(email);
    setEmailError(isValid ? '' : 'Please enter a valid email address');
    return isValid;
  };

  const validatePhone = (phone: string): boolean => {
    // Simple validation - requires at least 10 digits
    const phoneRegex = /^[\d\+\-\(\) ]{10,15}$/;
    const isValid = phoneRegex.test(phone);
    setPhoneError(isValid ? '' : 'Please enter a valid phone number');
    return isValid;
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    updateWizardData({
      personal: {
        ...personal,
        contact: {
          ...personal.contact,
          email: newEmail,
        },
      },
    });
    validateEmail(newEmail);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPhone = e.target.value;
    updateWizardData({
      personal: {
        ...personal,
        contact: {
          ...personal.contact,
          phone: newPhone,
        },
      },
    });
    validatePhone(newPhone);
  };

  const getAnalysisFromAI = async () => {
    setAiLoading(true);
    setAiError(null);
    
    try {
      // In a production environment, we would use the API service
      // const analysisResponse = await getAIAnalysis(wizardData);
      
      // For demo/development purposes, we'll use the local simulation
      await new Promise(resolve => setTimeout(resolve, 2000));
      const analysisResponse = analyzeRetirementPlan();
      
      setAiResponse(analysisResponse);
    } catch (error) {
      console.error('Error getting AI analysis:', error);
      setAiError('Failed to get AI analysis. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  // This is a placeholder function that simulates what an AI would analyze
  // In a real implementation, this would be replaced by an API call to OpenAI
  const analyzeRetirementPlan = (): AIAnalysisResponse => {
    // Create an array to hold analysis points
    const points: Array<{ type: 'warning' | 'info' | 'success', text: string }> = [];
    
    // Age-based analysis
    const age = parseInt(calculateAge(personal.client1.dateOfBirth) || '0');
    const partnerAge = personal.hasPartner ? parseInt(calculateAge(personal.client2.dateOfBirth) || '0') : 0;
    
    // ----------------------
    // Retirement age analysis
    // ----------------------
    if (employment.client1.expectedRetirementAge && employment.client1.expectedRetirementAge < 62) {
      points.push({
        type: 'warning',
        text: `You plan to retire at age ${employment.client1.expectedRetirementAge}, which is earlier than the minimum age (62) for Social Security benefits.`
      });
    }
    
    if (employment.client1.expectedRetirementAge && employment.client1.expectedRetirementAge > 70) {
      points.push({
        type: 'info',
        text: `You're planning to retire at age ${employment.client1.expectedRetirementAge}, which is later than when maximum Social Security benefits are achieved (age 70).`
      });
    }

    // Retirement age gap analysis for couples
    if (personal.hasPartner && 
        employment.client1.expectedRetirementAge && 
        employment.client2.expectedRetirementAge) {
      
      const retirementAgeDiff = Math.abs(
        employment.client1.expectedRetirementAge - employment.client2.expectedRetirementAge
      );
      
      if (retirementAgeDiff >= 5) {
        points.push({
          type: 'info',
          text: `There's a ${retirementAgeDiff} year difference between your planned retirement ages. Consider how this might affect your lifestyle and finances during the transition period.`
        });
      }
    }
    
    // ----------------------
    // Savings analysis
    // ----------------------
    const totalSavings =
      savings.taxDeferred.reduce((sum, acc) => sum + acc.balance, 0) +
      savings.taxFree.reduce((sum, acc) => sum + acc.balance, 0) +
      savings.taxable.reduce((sum, acc) => sum + acc.balance, 0);
    
    // Tax diversification analysis
    const taxDeferredTotal = savings.taxDeferred.reduce((sum, acc) => sum + acc.balance, 0);
    const taxFreeTotal = savings.taxFree.reduce((sum, acc) => sum + acc.balance, 0);
    const taxableTotal = savings.taxable.reduce((sum, acc) => sum + acc.balance, 0);
    
    if (totalSavings > 0) {
      const taxDeferredPercent = (taxDeferredTotal / totalSavings) * 100;
      const taxFreePercent = (taxFreeTotal / totalSavings) * 100;
      const taxablePercent = (taxableTotal / totalSavings) * 100;
      
      // Check for tax diversification
      if (taxDeferredPercent > 80) {
        points.push({
          type: 'info',
          text: `${taxDeferredPercent.toFixed(0)}% of your savings are in tax-deferred accounts. Consider diversifying into tax-free accounts (like Roth) for more flexibility in retirement.`
        });
      }
      
      if (taxFreePercent > 80) {
        points.push({
          type: 'info',
          text: `${taxFreePercent.toFixed(0)}% of your savings are in tax-free accounts. While tax-free growth is beneficial, consider some balance with pre-tax savings for current tax benefits.`
        });
      }
      
      // Low emergency funds warning
      if (taxablePercent < 10 && totalSavings > 50000) {
        points.push({
          type: 'warning',
          text: `Only ${taxablePercent.toFixed(0)}% of your savings are in easily accessible taxable accounts. Consider building an emergency fund of 3-6 months of expenses.`
        });
      }
    }
    
    const yearsUntilRetirement = employment.client1.expectedRetirementAge ? employment.client1.expectedRetirementAge - age : 0;
    
    // Savings adequacy based on retirement timeline
    if (yearsUntilRetirement > 0) {
      const currentAnnualIncome = employment.client1.currentIncome || 0;
      const savingsToIncomeRatio = currentAnnualIncome > 0 ? totalSavings / currentAnnualIncome : 0;
      
      // ***** Adding James/Raygar Model
      //console.log("age", age);
      const savingsPerYearPriorToRetirement = employment.client1.currentIncome - goals.targetIncome;
      //console.log("savingsPerYearPriorToRetirement", savingsPerYearPriorToRetirement);
      const totalSavingsAccumulatedPriorToRetirement = yearsUntilRetirement * savingsPerYearPriorToRetirement;
      //console.log("totalSavingsAccumulatedPriorToRetirement", totalSavingsAccumulatedPriorToRetirement);
      //console.log("totalSavings", totalSavings);
      //console.log("yearsUntilRetirement", yearsUntilRetirement);
      const savingsAtRetirement = (totalSavings * Math.pow(1.02, yearsUntilRetirement)) + totalSavingsAccumulatedPriorToRetirement;
      //console.log("savingsAtRetirement", savingsAtRetirement);
      const savingsPerYearAfterRetirement = (socialSecurity.client1.projectedAmount ?? 0) + (socialSecurity.client2.projectedAmount ?? 0) + (employment.client2.pensionBenefitAmount || 0) + (employment.client1.pensionBenefitAmount || 0) - goals.targetIncome;
      //console.log("savingsPerYearAfterRetirement", savingsPerYearAfterRetirement);
      const yearsInRetirement = 95 - employment.client1.expectedRetirementAge;
      //console.log("yearsInRetirement", yearsInRetirement);
      const totalSavingsAccumulationInRetirement = yearsInRetirement * savingsPerYearAfterRetirement;
      //console.log("totalSavingsAccumulationInRetirement", totalSavingsAccumulationInRetirement);
      const totalSavingsAtEndOfPlan = savingsAtRetirement + totalSavingsAccumulationInRetirement;
      //console.log("totalSavingsAtEndOfPlan", totalSavingsAtEndOfPlan);
      const savingsAtEndAsPercentageOfSavingsAtRetirement = totalSavingsAtEndOfPlan / savingsAtRetirement;
      //console.log("savingsAtEndAsPercentageOfSavingsAtRetirement", savingsAtEndAsPercentageOfSavingsAtRetirement);

      if (savingsAtEndAsPercentageOfSavingsAtRetirement < 0.1) {
        points.push({
          type: 'warning',
          text: `Your current savings of ${formatCurrency(totalSavings)} may not be sufficient for your retirement target in ${yearsUntilRetirement} years. Consider increasing your savings rate.`
        });
      } else if (savingsAtEndAsPercentageOfSavingsAtRetirement > 0.4) {
        points.push({
          type: 'success',
          text: `You have a solid savings of ${formatCurrency(totalSavings)} for your retirement in ${yearsUntilRetirement} years. You're on track for a comfortable retirement.`
        });
      } else {
        points.push({
          type: 'info',
          text: `Your current savings of ${formatCurrency(totalSavings)} puts you on a reasonable path toward retirement in ${yearsUntilRetirement} years, but consider consulting with a financial advisor for optimization.`
        });
      }

      // Late career saving opportunities
      if (yearsUntilRetirement < 10 && yearsUntilRetirement > 0 && age >= 50) {
        points.push({
          type: 'info',
          text: `You're within 10 years of retirement. Consider taking advantage of catch-up contributions to your retirement accounts, which allow you to save more once you're over 50.`
        });
      }
    }
    
    // ----------------------
    // Social Security analysis
    // ----------------------
    if (socialSecurity.client1.claimingAge && socialSecurity.client1.claimingAge < 67) {
      points.push({
        type: 'info',
        text: `You plan to claim Social Security at age ${socialSecurity.client1.claimingAge}. Waiting until full retirement age (67) would increase your benefit amount.`
      });
    }
    
    // Social Security maximization strategy for couples
    if (personal.hasPartner && 
        socialSecurity.client1.claimingAge && 
        socialSecurity.client2.claimingAge) {
      
      // If there's a large benefit disparity, suggest maximizing the higher benefit
      if ((socialSecurity.client1.projectedAmount ?? 0) > (socialSecurity.client2.projectedAmount ?? 0) * 1.5) {
        points.push({
          type: 'info',
          text: `${personal.client1.firstName}'s projected Social Security benefit is significantly higher. Consider having ${personal.client1.firstName} delay claiming until age 70 to maximize lifetime benefits.`
        });
      } else if ((socialSecurity.client2.projectedAmount ?? 0) > (socialSecurity.client1.projectedAmount ?? 0) * 1.5) {
        points.push({
          type: 'info',
          text: `${personal.client2.firstName}'s projected Social Security benefit is significantly higher. Consider having ${personal.client2.firstName} delay claiming until age 70 to maximize lifetime benefits.`
        });
      }
    }

    // ----------------------
    // Pension analysis
    // ----------------------
    if (employment.client1.hasPension && employment.client1.pensionBenefitAmount) {
      const pensionToIncomeRatio = employment.client1.pensionBenefitAmount / (employment.client1.currentIncome || 1);
      
      if (pensionToIncomeRatio > 0.5) {
        points.push({
          type: 'success',
          text: `Your pension benefit (${formatCurrency(employment.client1.pensionBenefitAmount)}) represents a significant portion of your income, providing a solid foundation for retirement security.`
        });
      }
      
      // Age gap between retirement and pension start
      if (employment.client1.expectedRetirementAge && 
          employment.client1.pensionStartAge && 
          employment.client1.pensionStartAge > employment.client1.expectedRetirementAge) {
        
        const gapYears = employment.client1.pensionStartAge - employment.client1.expectedRetirementAge;
        if (gapYears > 2) {
          points.push({
            type: 'warning',
            text: `There's a ${gapYears}-year gap between your retirement age and when your pension begins. Ensure you have adequate savings to bridge this income gap.`
          });
        }
      }
    }

    // ----------------------
    // Goal-based analysis
    // ----------------------
    if (goals.targetIncome) {
      // Calculate estimated retirement income
      const estimatedSSBenefit = (socialSecurity.client1.currentAmount || socialSecurity.client1.projectedAmount || 0);

      const partnerSSBenefit = personal.hasPartner
        ? (socialSecurity.client2.currentAmount || socialSecurity.client2.projectedAmount || 0)
        : 0;

      const pensionBenefit = employment.client1.hasPension ? (employment.client1.pensionBenefitAmount || 0) : 0;
      const partnerPensionBenefit = (personal.hasPartner && employment.client2.hasPension)
        ? (employment.client2.pensionBenefitAmount || 0)
        : 0;
      
      // Rough estimate of income from savings (using 4% rule)
      const savingsIncome = totalSavings * 0.04;
      
      const totalEstimatedIncome = estimatedSSBenefit + partnerSSBenefit + 
                                 pensionBenefit + partnerPensionBenefit + 
                                 savingsIncome;
      
      // Compare to target
      const incomeGap = goals.targetIncome - totalEstimatedIncome;
    }

    // ----------------------
    // Age-specific recommendations
    // ----------------------
    if (age < 35) {
      points.push({
        type: 'info',
        text: `At age ${age}, time is your greatest asset for retirement planning. Consider maximizing contributions to tax-advantaged accounts to benefit from long-term compound growth.`
      });
    } else if (age >= 60 && !employment.client1.isRetired) {
      points.push({
        type: 'info',
        text: `As you approach retirement, consider meeting with a financial advisor to develop a detailed withdrawal strategy and review your asset allocation for appropriate risk levels.`
      });
    }
    
    // General feedback text based on the points
    let feedback = "Based on the information you've provided, here's an analysis of your retirement plan:\n\n";
    
    if (points.filter(p => p.type === 'warning').length > 0) {
      feedback += "There are some areas of concern that may need attention. ";
    } else if (points.filter(p => p.type === 'info').length > 0) {
      feedback += "Your retirement plan looks reasonable, with some opportunities for optimization. ";
    } else {
      feedback += "Your retirement plan appears to be on solid footing. ";
    }
    
    feedback += "Review the specific points below for more details and consider consulting with a qualified financial advisor for personalized advice.";
    
    return {
      feedback,
      points
    };
  };

  // Helper function to check if required fields are complete
  const checkRequiredFields = () => {
    const incompleteSteps: string[] = [];
    
    // Check Personal Info
    if (!personal.client1.firstName || !personal.client1.lastName || !personal.client1.dateOfBirth) {
      incompleteSteps.push('Personal Information');
    } else if (personal.hasPartner && (!personal.client2.firstName || !personal.client2.lastName || !personal.client2.dateOfBirth)) {
      incompleteSteps.push('Partner Information');
    }
    
    // Check Social Security
    if (!socialSecurity.client1.currentAmount && !socialSecurity.client1.projectedAmount && !socialSecurity.client1.claimingAge) {
      incompleteSteps.push('Social Security');
    }

    // Check Savings
    const grandTotal =
      savings.taxDeferred.reduce((sum, acc) => sum + acc.balance, 0) +
      savings.taxFree.reduce((sum, acc) => sum + acc.balance, 0) +
      savings.taxable.reduce((sum, acc) => sum + acc.balance, 0);
    
    if (grandTotal <= 0) {
      incompleteSteps.push('Savings & Investments');
    }
    
    // Check Goals
    if (!goals.targetIncome || goals.targetIncome <= 0) {
      incompleteSteps.push('Annual Cashflow (After-Tax');
    }
    
    return incompleteSteps;
  };
  
  const incompleteSteps = checkRequiredFields();

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Typography variant="h6" gutterBottom>
        Review Your Financial Plan
      </Typography>
      
      <Typography variant="body1" paragraph>
        Please review your information below to ensure everything is accurate before submission.
      </Typography>
      
      {incompleteSteps.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" gutterBottom>
            <strong>Some required information is missing:</strong>
          </Typography>
          <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
            {incompleteSteps.map((step, index) => (
              <li key={index}>
                <Typography variant="body2">{step}</Typography>
              </li>
            ))}
          </ul>
          <Typography variant="body2">
            Please go back and complete all required fields before submitting.
          </Typography>
        </Alert>
      )}
      
      <Box sx={{ mb: 4 }}>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="bold">Personal Information</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography>Name: {personal.client1.firstName} {personal.client1.lastName}</Typography>
              <Typography>Date of Birth: {formatDate(personal.client1.dateOfBirth)} (Age: {calculateAge(personal.client1.dateOfBirth)})</Typography>
              
              {personal.hasPartner && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2">Partner Information</Typography>
                  <Typography>Name: {personal.client2.firstName} {personal.client2.lastName}</Typography>
                  <Typography>Date of Birth: {formatDate(personal.client2.dateOfBirth)} (Age: {calculateAge(personal.client2.dateOfBirth)})</Typography>
                </>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
        
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="bold">Employment & Income</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography>Current Status: {employment.client1.isRetired ? 'Retired' : 'Working'}</Typography>
              {!employment.client1.isRetired && (
                <>
                  <Typography>Current Annual Income: {formatCurrency(employment.client1.currentIncome)}</Typography>
                  <Typography>Expected Retirement Age: {employment.client1.expectedRetirementAge}</Typography>
                </>
              )}
              
              {personal.hasPartner && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2">Partner Employment</Typography>
                  <Typography>Current Status: {employment.client2.isRetired ? 'Retired' : 'Working'}</Typography>
                  {!employment.client2.isRetired && (
                    <>
                      <Typography>Current Annual Income: {formatCurrency(employment.client2.currentIncome)}</Typography>
                      <Typography>Expected Retirement Age: {employment.client2.expectedRetirementAge}</Typography>
                    </>
                  )}
                </>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
        
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="bold">Social Security & Pensions</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {socialSecurity.client1.currentAmount ? (
                <Typography>Current Annual Social Security Benefit: {formatCurrency(socialSecurity.client1.currentAmount)}</Typography>
              ) : (
                <Typography>Estimated Annual Future Benefit: {formatCurrency(socialSecurity.client1.projectedAmount ?? 0)}</Typography>
              )}
              {socialSecurity.client1.claimingAge && (
                <Typography>Planned Claiming Age: {socialSecurity.client1.claimingAge}</Typography>
              )}

              <Typography>Pension: {employment.client1.hasPension ? 'Yes' : 'No'}</Typography>
              {employment.client1.hasPension && (
                <>
                  <Typography>Pension Starting Age: {employment.client1.pensionStartAge}</Typography>
                  <Typography>Pension Benefit Amount: {formatCurrency(employment.client1.pensionBenefitAmount)} annually</Typography>
                </>
              )}

              {personal.hasPartner && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2">Partner Social Security & Pensions</Typography>
                  {socialSecurity.client2.currentAmount ? (
                    <Typography>Partner's Current Annual Benefit: {formatCurrency(socialSecurity.client2.currentAmount)}</Typography>
                  ) : (
                    <Typography>Partner's Estimated Annual Future Benefit: {formatCurrency(socialSecurity.client2.projectedAmount ?? 0)}</Typography>
                  )}
                  {socialSecurity.client2.claimingAge && (
                    <Typography>Partner's Planned Claiming Age: {socialSecurity.client2.claimingAge}</Typography>
                  )}

                  <Typography>Partner Pension: {employment.client2.hasPension ? 'Yes' : 'No'}</Typography>
                  {employment.client2.hasPension && (
                    <>
                      <Typography>Partner Pension Starting Age: {employment.client2.pensionStartAge}</Typography>
                      <Typography>Partner Pension Benefit Amount: {formatCurrency(employment.client2.pensionBenefitAmount)} annually</Typography>
                    </>
                  )}
                </>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
        
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="bold">Savings & Investments</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="subtitle2">Tax-Deferred Accounts (401k, IRA, etc.)</Typography>
              <Typography>Your Balance: {formatCurrency(savings.taxDeferred.filter(a => a.owner === 'client1').reduce((sum, a) => sum + a.balance, 0))}</Typography>
              {personal.hasPartner && (
                <Typography>Partner's Balance: {formatCurrency(savings.taxDeferred.filter(a => a.owner === 'client2').reduce((sum, a) => sum + a.balance, 0))}</Typography>
              )}

              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2">Tax-Free Accounts (Roth IRA, etc.)</Typography>
              <Typography>Your Balance: {formatCurrency(savings.taxFree.filter(a => a.owner === 'client1').reduce((sum, a) => sum + a.balance, 0))}</Typography>
              {personal.hasPartner && (
                <Typography>Partner's Balance: {formatCurrency(savings.taxFree.filter(a => a.owner === 'client2').reduce((sum, a) => sum + a.balance, 0))}</Typography>
              )}

              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2">Taxable Accounts</Typography>
              {personal.hasPartner ? (
                <Typography>Joint Balance: {formatCurrency(savings.taxable.filter(a => a.owner === 'joint').reduce((sum, a) => sum + a.balance, 0))}</Typography>
              ) : (
                <Typography>Your Balance: {formatCurrency(savings.taxable.filter(a => a.owner === 'client1').reduce((sum, a) => sum + a.balance, 0))}</Typography>
              )}

              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" fontWeight="bold">Total Savings</Typography>
              <Typography fontWeight="bold">
                {formatCurrency(
                  savings.taxDeferred.reduce((sum, acc) => sum + acc.balance, 0) +
                  savings.taxFree.reduce((sum, acc) => sum + acc.balance, 0) +
                  savings.taxable.reduce((sum, acc) => sum + acc.balance, 0)
                )}
              </Typography>
            </Box>
          </AccordionDetails>
        </Accordion>
        
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1" fontWeight="bold">Retirement Goals</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography>Annual Cashflow (After-Tax): {formatCurrency(goals.targetIncome)}</Typography>
              
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Selected Goals:</Typography>
              {goals.selected.financialIndependence && (<Typography>• Financial Independence</Typography>)}
              {goals.selected.travel && (<Typography>• Travel & Adventure</Typography>)}
              {goals.selected.legacy && (<Typography>• Leave a Legacy</Typography>)}
              {goals.selected.healthcare && (<Typography>• Healthcare Security</Typography>)}
              {goals.selected.lifestyle && (<Typography>• Lifestyle Maintenance</Typography>)}
              {goals.selected.other && (
                <>
                  <Typography>• Other Goals</Typography>
                  <Typography variant="body2" sx={{ ml: 2, fontStyle: 'italic' }}>
                    "{goals.otherGoalDescription}"
                  </Typography>
                </>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>
      
      {/* AI Analysis Section */}
      <Paper sx={{ p: 3, mb: 4, bgcolor: '#f8f9fa' }}>
        <Typography variant="h6" gutterBottom>
          Retirement Opportunity Assessment
        </Typography>
        
        <Typography variant="body2" paragraph>
        Get immediate feedback on your opportunities to achieve your retirement goals.
        </Typography>
        
        {aiError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {aiError}
          </Alert>
        )}
        
        {!aiResponse && !aiLoading && (
          <Button 
            variant="contained" 
            color="primary" 
            onClick={getAnalysisFromAI}
            sx={{ mt: 1 }}
          >
            Analyze My Plan
          </Button>
        )}
        
        {aiLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 2 }}>
            <CircularProgress size={24} />
            <Typography>Analyzing your financial plan...</Typography>
          </Box>
        )}
        
        {aiResponse && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" paragraph>
              {aiResponse.feedback}
            </Typography>
            
            <Box sx={{ mt: 2 }}>
              {aiResponse.points.map((point, index) => (
                <Box 
                  key={index} 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: 1, 
                    mb: 1.5,
                    p: 1,
                    borderRadius: 1,
                    bgcolor: 
                      point.type === 'warning' ? 'rgba(255, 152, 0, 0.1)' : 
                      point.type === 'success' ? 'rgba(76, 175, 80, 0.1)' : 
                      'rgba(33, 150, 243, 0.1)'
                  }}
                >
                  {point.type === 'warning' && <WarningIcon color="warning" />}
                  {point.type === 'success' && <CheckCircleIcon color="success" />}
                  {point.type === 'info' && <InfoIcon color="info" />}
                  <Typography variant="body2">{point.text}</Typography>
                </Box>
              ))}
            </Box>
            
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              This is an automated analysis and should not replace professional financial advice. Please consult with a qualified financial advisor for personalized recommendations.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Contact Information Section */}
      <Box sx={{ 
        mt: 4, 
        p: 3, 
        borderRadius: 2,
        backgroundColor: 'rgba(144, 202, 249, 0.2)', // Darker light blue background
        border: '1px solid rgba(144, 202, 249, 0.5)' // Darker light blue border
      }}>
        <Typography variant="h6" gutterBottom>
          Get Your Detailed PlanScout Plan
        </Typography>
        <Typography variant="body1" paragraph>
          {isDemoAdvisor ? (
            "To receive your demo plan, please provide your information below. Once you're onboarded, this is where your prospects will enter their contact details."
          ) : isFullAutomation ? (
            advisor ? (
              <>{formatAdvisorName(advisor.name)} will contact you as soon as your plan is ready.</>
            ) : (
              "Your financial advisor will contact you as soon as your plan is ready."
            )
          ) : hasCustomerId ? (
            advisor ? (
              <>{formatAdvisorName(advisor.name)} will contact you as soon as your plan is ready.</>
            ) : (
              "Your financial advisor will contact you as soon as your plan is ready."
            )
          ) : (
            "We'll email you as soon as your plan is ready, along with simple instructions to access it through our platform."
          )}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {isDemoAdvisor && (
            <>
              <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
                <TextField
                  fullWidth
                  label="Advisor First Name"
                  value={personal.contact.advisorFirstName || advisorFirstName}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setAdvisorFirstName(newValue);
                    updateWizardData({
                      personal: {
                        ...personal,
                        contact: {
                          ...personal.contact,
                          advisorFirstName: newValue,
                        },
                      },
                    });
                  }}
                />
              </Box>
              <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
                <TextField
                  fullWidth
                  label="Advisor Last Name"
                  value={personal.contact.advisorLastName || advisorLastName}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setAdvisorLastName(newValue);
                    updateWizardData({
                      personal: {
                        ...personal,
                        contact: {
                          ...personal.contact,
                          advisorLastName: newValue,
                        },
                      },
                    });
                  }}
                />
              </Box>
            </>
          )}
          <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
            <TextField
              fullWidth
              label="Email"
              value={personal.contact.email || ''}
              onChange={handleEmailChange}
              error={!!emailError}
              helperText={emailError}
              onBlur={() => personal.contact.email && validateEmail(personal.contact.email)}
            />
          </Box>
          <Box sx={{ flex: '1 1 45%', minWidth: '250px' }}>
            <TextField
              fullWidth
              label="Phone Number"
              value={personal.contact.phone || ''}
              onChange={handlePhoneChange}
              error={!!phoneError}
              helperText={phoneError}
              onBlur={() => personal.contact.phone && validatePhone(personal.contact.phone)}
            />
          </Box>
        </Box>
      </Box>

      {/* Subtle "Powered by PlanScout" footer */}
      {advisor?.features?.branding?.showPoweredByPlanScout !== false && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          mt: 4,
          opacity: 0.6,
          '&:hover': {
            opacity: 0.8
          }
        }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            Powered by
          </Typography>
          <Box
            component="img"
            src={planScoutLogo}
            alt="PlanScout"
            sx={{
              height: 30,
              width: 'auto'
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default ReviewStep; 